import { memo, useCallback, useEffect, useMemo } from 'react';
import { Key } from 'react-aria-components';

import LogicalExpressionIcon from '@/assets/logical-expression-icon.svg?react';
import { merge } from '@/components';
import {
  Button,
  Flex,
  ListBox,
  ListBoxItem,
  Pills,
  Popover,
  Select,
  SelectValue,
  Separator,
  Text,
} from '@/components/core';
import { ChevronIcon } from '@/components/icons';
import { findField, FormatterValidatorHeader, ValidationFormSettings } from '@/components/ui';
import { FORMATTER_NUMERIC_FUNCTION_ID } from '@/constants';
import { useValidatorMutation } from '@/hooks';
import { useModelConfigStore } from '@/store';

const DEFAULT_OPERATOR = 'eq';

interface Item extends Pills.Item {
  isVariable: boolean;
}

type LogicalExpressionProps = {
  onItemsChanged?: (items: Pills.Item[]) => void;
  defaultValue?: Item[];
};

const ILogicalExpression = ({ onItemsChanged, defaultValue }: LogicalExpressionProps) => {
  const getFields = useModelConfigStore((state) => state.getFields);
  const fields = useModelConfigStore((state) => state.fields);

  const variables: Item[] = useMemo(
    () =>
      fields
        .filter((field) => field.formatters?.[0]?.functionId === FORMATTER_NUMERIC_FUNCTION_ID)
        .map((field) => ({
          label: field.name,
          value: field.id,
          isVariable: true,
        })),
    [fields]
  );

  const colorize = useCallback(
    (value: string) => {
      const ok = 'bg-success-100';

      const fields = getFields();
      if (!isNaN(parseFloat(value))) return ok;
      if (['+', '-', '*', '/'].includes(value)) return ok;
      if (fields.map((f) => f.id).includes(value)) return ok;

      return 'bg-error-100';
    },
    [getFields]
  );

  const mapValue = useCallback(
    (key: Key): Item => {
      const fieldId = key.toString();

      /* Try to find by id first */
      let field = getFields().find((v) => v.id === fieldId);
      if (field) return { label: field.name, value: field.id, isVariable: true };

      /* Then try to find by name */
      field = getFields().find((v) => (v.name ?? '').toLowerCase() === fieldId.toLowerCase());
      if (field) return { label: field.name, value: field.id, isVariable: true };

      return { value: key.toString(), isVariable: false };
    },
    [getFields]
  );

  return (
    <Flex className="gap-xs items-center">
      <Pills.Input className="flex grow" mapValue={mapValue}>
        <Pills.List
          autoSeparate={['+', '-', '*', '/']}
          onItemsChange={onItemsChanged}
          initialValues={defaultValue}
          mapValue={mapValue}
        >
          {(pill: Pills.Item) => (
            <span
              key={pill.value}
              className={merge('mx-1 rounded-md border-2 border-gray-200 px-2 py-0', colorize(pill.value))}
            >
              {pill.label || pill.value}
            </span>
          )}
        </Pills.List>

        <Select>
          <Flex>
            <Button variant="plain">
              <LogicalExpressionIcon className="size-4 stroke-purple-500" />
            </Button>
          </Flex>

          <Popover>
            <ListBox>
              {variables.map((v: Pills.Item) => (
                <ListBoxItem key={v.value} id={v.value}>
                  <Text>{v.label}</Text>
                </ListBoxItem>
              ))}
            </ListBox>
          </Popover>
        </Select>
      </Pills.Input>
    </Flex>
  );
};

const LogicalExpression = memo(ILogicalExpression) as typeof ILogicalExpression;

export type LogexValidatorProps = {
  fieldId: string;
  validatorId: string;
};

const ILogexValidator = ({ fieldId, validatorId }: LogexValidatorProps) => {
  const field = useModelConfigStore((state) => findField(state.fields, fieldId));
  const getFields = useModelConfigStore((state) => state.getFields);
  const mutate = useValidatorMutation({ fieldId, validatorId, parentId: field?.parentId });
  const validator = field?.validators?.find((v) => v.id === validatorId);

  const updateExpression = useCallback(
    (expr: string, items: Pills.Item[]) => {
      mutate((v) => {
        v.config[expr] = items.map((it) => it as object);
        v.config.variables = getFields().map((f) => ({ id: f.id }));
      });
    },
    [getFields, mutate]
  );

  const lhs = useCallback((items: Pills.Item[]) => updateExpression('lhsExpression', items), [updateExpression]);
  const rhs = useCallback((items: Pills.Item[]) => updateExpression('rhsExpression', items), [updateExpression]);

  useEffect(() => {
    if (!validator?.config) return;
    if (validator.config.operator) return;
    mutate((v) => (v.config.operator = DEFAULT_OPERATOR));
  }, [mutate, validator?.config]);

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <FormatterValidatorHeader validator={validator} />
      <Separator orientation="horizontal" />

      <Flex col className="gap-4">
        <LogicalExpression defaultValue={validator?.config?.lhsExpression} onItemsChanged={lhs} />

        <Select
          defaultSelectedKey={validator?.config?.operator ?? DEFAULT_OPERATOR}
          onSelectionChange={(op) => mutate((v) => (v.config.operator = op.toString()))}
          className="flex items-start"
        >
          <div>
            <Button variant="secondary">
              <SelectValue />
              <ChevronIcon rotation="down" />
            </Button>
          </div>

          <Popover>
            <ListBox>
              <ListBoxItem id="eq">
                <Text>=</Text>
              </ListBoxItem>
              <ListBoxItem id="lt">
                <Text>{'<'}</Text>
              </ListBoxItem>
              <ListBoxItem id="gt">
                <Text>{'>'}</Text>
              </ListBoxItem>
              <ListBoxItem id="lte">
                <Text>{'<='}</Text>
              </ListBoxItem>
              <ListBoxItem id="gte">
                <Text>{'>='}</Text>
              </ListBoxItem>
            </ListBox>
          </Popover>
        </Select>

        <LogicalExpression defaultValue={validator?.config?.rhsExpression} onItemsChanged={rhs} />
      </Flex>

      <Separator orientation="horizontal" />

      <ValidationFormSettings fieldId={fieldId} validatorId={validatorId} />
    </div>
  );
};

export const LogexValidator = memo(ILogexValidator) as typeof ILogexValidator;
